// Family tree hero banner — display + admin manager (gallery / AI / upload).
// Premium perk: backend returns 402 for free-plan admins → UpgradeModal.
import { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Image as ImageIcon, Sparkles, Upload, X, Crown } from "lucide-react";

const AI_STYLES = [
  { key: "kerala_floral", label: "Kerala Floral" },
  { key: "hibiscus_coastal", label: "Hibiscus Coastal" },
  { key: "marigold_grand", label: "Marigold Grand" },
  { key: "sikh_golden_temple", label: "Golden Temple" },
  { key: "christian_heritage", label: "Christian Heritage" },
  { key: "islamic_elegant", label: "Islamic Elegant" },
  { key: "banyan_legacy", label: "Banyan Legacy" },
];

export default function TreeBanner({ tree, isAdmin, onChanged }) {
  const [open, setOpen] = useState(false);
  if (!tree?.banner_url && !isAdmin) return null;
  return (
    <div className="mb-6">
      {tree?.banner_url ? (
        <div className="relative rounded-2xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
          <img src={tree.banner_url} alt={`${tree.name} banner`} className="w-full object-cover" style={{ aspectRatio: "3.2/1" }} data-testid="tree-banner-image" />
          {isAdmin && (
            <button onClick={() => setOpen(true)} className="absolute top-2 right-2 rounded-full px-3 py-1 text-xs font-semibold text-white shadow" style={{ background: "rgba(0,0,0,0.55)" }} data-testid="tree-banner-edit-btn">
              Change banner
            </button>
          )}
        </div>
      ) : (
        <button onClick={() => setOpen(true)} className="w-full rounded-2xl border-2 border-dashed py-5 text-sm font-semibold flex items-center justify-center gap-2 transition hover:bg-black/5" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }} data-testid="tree-banner-create-btn">
          <Crown className="w-4 h-4" style={{ color: "#B8860B" }} />
          Create your family banner — AI-designed or from our gallery
        </button>
      )}
      {open && <BannerModal tree={tree} onClose={() => setOpen(false)} onChanged={onChanged} />}
    </div>
  );
}

function BannerModal({ tree, onClose, onChanged }) {
  const [tab, setTab] = useState("gallery");
  const [templates, setTemplates] = useState(null);
  const [selected, setSelected] = useState(null);
  const [style, setStyle] = useState("kerala_floral");
  const [familyName, setFamilyName] = useState(tree.name || "");
  const [location, setLocation] = useState(tree.country === "India" ? "India" : (tree.country || ""));
  const [motto, setMotto] = useState("Love · Legacy · Togetherness");
  const [job, setJob] = useState(null); // {id,status,url,error}
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    http.get(`/family-trees/${tree.id}/banner-templates`).then((r) => setTemplates(r.data.items || [])).catch(() => setTemplates([]));
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [tree.id]);

  const fail402 = (err) => {
    if (err?.response?.status === 402) {
      window.dispatchEvent(new CustomEvent("ift:upgrade-required", { detail: { message: err.response?.data?.detail } }));
      onClose();
      return true;
    }
    return false;
  };

  const startJob = async (mode) => {
    if (!familyName.trim()) { toast.error("Enter your family name"); return; }
    setBusy(true); setJob(null);
    try {
      const { data } = await http.post(`/family-trees/${tree.id}/banner/start`, {
        mode, template_id: selected?.id || null, style,
        family_name: familyName, location, motto,
      });
      setJob({ id: data.job_id, status: "generating" });
      pollRef.current = setInterval(async () => {
        try {
          const r = await http.get(`/family-trees/banner-jobs/${data.job_id}`);
          if (r.data.status !== "generating") {
            clearInterval(pollRef.current);
            setJob(r.data);
            setBusy(false);
            if (r.data.status === "error") toast.error(r.data.error || "Generation failed");
          }
        } catch { /* keep polling */ }
      }, 3500);
    } catch (err) {
      setBusy(false);
      if (!fail402(err)) toast.error(err?.response?.data?.detail || "Could not start generation");
    }
  };

  const applyBanner = async (url) => {
    try {
      await http.post(`/family-trees/${tree.id}/banner/set`, { url });
      toast.success("Family banner updated!");
      onChanged?.(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to set banner"); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await http.post(`/family-trees/${tree.id}/banner/upload`, fd);
      toast.success("Banner uploaded!");
      onChanged?.(); onClose();
    } catch (err) {
      if (!fail402(err)) toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const TabBtn = ({ k, label, Icon }) => (
    <button onClick={() => setTab(k)} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold border transition ${tab === k ? "text-white" : ""}`} style={tab === k ? { background: "#B25D38", borderColor: "#B25D38" } : { borderColor: "var(--wofy-border)" }} data-testid={`banner-tab-${k}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[80] flex justify-center bg-black/60 p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-3xl my-auto rounded-2xl bg-white shadow-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="banner-modal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-2xl">Family banner</h3>
            <p className="text-xs opacity-60 mt-0.5">A beautiful custom board shown at the top of your tree and on listings.</p>
          </div>
          <button onClick={onClose} data-testid="banner-modal-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-2 mt-4 flex-wrap">
          <TabBtn k="gallery" label="Gallery" Icon={ImageIcon} />
          <TabBtn k="ai" label="AI Create" Icon={Sparkles} />
          <TabBtn k="upload" label="Upload" Icon={Upload} />
        </div>

        {tab !== "upload" && (
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            <label className="block sm:col-span-1"><div className="text-[10px] font-mono uppercase opacity-60 mb-1">Family name</div>
              <input value={familyName} onChange={(e) => setFamilyName(e.target.value)} className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-border)" }} data-testid="banner-family-name" /></label>
            <label className="block sm:col-span-1"><div className="text-[10px] font-mono uppercase opacity-60 mb-1">Location</div>
              <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Kerala, India" className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-border)" }} data-testid="banner-location" /></label>
            <label className="block sm:col-span-1"><div className="text-[10px] font-mono uppercase opacity-60 mb-1">Motto</div>
              <input value={motto} onChange={(e) => setMotto(e.target.value)} className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-border)" }} data-testid="banner-motto" /></label>
          </div>
        )}

        {tab === "gallery" && (
          <div className="mt-4">
            {!templates ? <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
                {templates.map((t) => (
                  <button key={t.id} onClick={() => setSelected(t)} className={`relative rounded-lg overflow-hidden border-2 transition ${selected?.id === t.id ? "" : "border-transparent"}`} style={selected?.id === t.id ? { borderColor: "#B25D38" } : {}} data-testid={`banner-template-${t.id}`}>
                    <img src={t.image_url} alt={t.title} className="w-full object-cover" style={{ aspectRatio: "3.2/1" }} loading="lazy" />
                    <span className="absolute bottom-0 inset-x-0 text-[10px] text-white px-1.5 py-0.5 truncate text-left" style={{ background: "rgba(0,0,0,0.5)" }}>{t.title}</span>
                  </button>
                ))}
              </div>
            )}
            <p className="text-[11px] opacity-60 mt-2">Pick a board — the AI redraws it with YOUR family name, location and motto.</p>
            <button disabled={!selected || busy} onClick={() => startJob("template")} className="mt-3 rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-40 inline-flex items-center gap-2" style={{ background: "#B25D38" }} data-testid="banner-generate-from-template">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Personalize this board
            </button>
          </div>
        )}

        {tab === "ai" && (
          <div className="mt-4">
            <div className="text-[10px] font-mono uppercase opacity-60 mb-1">Style</div>
            <div className="flex gap-1.5 flex-wrap">
              {AI_STYLES.map((s) => (
                <button key={s.key} onClick={() => setStyle(s.key)} className={`rounded-full px-3 py-1 text-xs border font-semibold ${style === s.key ? "text-white" : ""}`} style={style === s.key ? { background: "#B25D38", borderColor: "#B25D38" } : { borderColor: "var(--wofy-border)" }} data-testid={`banner-style-${s.key}`}>{s.label}</button>
              ))}
            </div>
            <button disabled={busy} onClick={() => startJob("ai")} className="mt-4 rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-40 inline-flex items-center gap-2" style={{ background: "#B25D38" }} data-testid="banner-generate-ai">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate with AI
            </button>
          </div>
        )}

        {tab === "upload" && (
          <div className="mt-4">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="banner-upload-input" />
            <button disabled={busy} onClick={() => fileRef.current?.click()} className="w-full rounded-xl border-2 border-dashed py-10 text-sm font-semibold flex flex-col items-center gap-2" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }} data-testid="banner-upload-btn">
              {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
              Upload your own banner (wide image, up to 8 MB)
            </button>
          </div>
        )}

        {job?.status === "generating" && (
          <div className="mt-4 rounded-xl px-4 py-3 text-sm flex items-center gap-2" style={{ background: "#FFF7ED", color: "#92400E" }} data-testid="banner-job-progress">
            <Loader2 className="w-4 h-4 animate-spin" /> Painting your family banner… this takes 20–60 seconds.
          </div>
        )}
        {job?.status === "done" && job.url && (
          <div className="mt-4" data-testid="banner-job-result">
            <img src={job.url} alt="Generated banner preview" className="w-full rounded-xl border object-cover" style={{ borderColor: "var(--wofy-border)", aspectRatio: "3.2/1" }} />
            <div className="flex gap-2 mt-2">
              <button onClick={() => applyBanner(job.url)} className="rounded-full px-5 py-2 text-sm font-semibold text-white" style={{ background: "#166534" }} data-testid="banner-use-btn">Use this banner</button>
              <button onClick={() => setJob(null)} className="rounded-full px-4 py-2 text-sm border" style={{ borderColor: "var(--wofy-border)" }} data-testid="banner-retry-btn">Try again</button>
            </div>
          </div>
        )}

        {tree.banner_url && (
          <button onClick={() => applyBanner("")} className="mt-5 text-xs underline opacity-60 hover:opacity-100" data-testid="banner-remove-btn">Remove current banner</button>
        )}
      </div>
    </div>
  );
}
